/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useRef } from "react";

import * as React from "react";
import { HomePageButton } from "components/shared/HomePageButton";
import styled from "styled-components";
import { useStudioAcceptedFileTypes } from "hooks/useStudioAcceptedFileTypes";
import { ProgressCircle } from "components/Studio/Export/shared/ProgressCircle";

const Button = styled(HomePageButton)`
  display: flex;
  width: unset;
  height: 56px;
  border-radius: 28px;
  justify-content: center;
  font-size: 17px;
`;

const ButtonText = styled.span`
  padding: 0 4px;
`;

interface ChooseMediaButtonParams {
  disabled: boolean;
  handleFile: (file: File) => Promise<void>;
  videoOnly?: boolean;
}

const ProgressCircleContainer = styled.div`
  padding-top: 4px;
  margin-left: -4px;
  margin-right: 8px;
`;

const ChooseMediaButton: React.FC<ChooseMediaButtonParams> = ({
  disabled,
  handleFile,
  videoOnly = false,
}: ChooseMediaButtonParams) => {
  const acceptedFileTypes = useStudioAcceptedFileTypes(videoOnly);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    fileInputRef.current?.click();
    event.stopPropagation();
  };

  const startTranscribeAudio = useCallback(async () => {
    const selectedFiles = fileInputRef.current?.files;

    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    await handleFile(selectedFiles[0]);
  }, [fileInputRef, handleFile]);

  return (
    <>
      <Button
        aria-label={I18n.t("use_cases.transcription_page.choose_file_button")}
        onClick={onClick}
        disabled={disabled}
      >
        {disabled ? (
          <ProgressCircleContainer>
            <ProgressCircle progressPercentage={null} />
          </ProgressCircleContainer>
        ) : null}
        <ButtonText>
          {I18n.t("use_cases.transcription_page.choose_file_button")}
        </ButtonText>
        <input
          type="file"
          ref={fileInputRef}
          onChange={startTranscribeAudio}
          hidden
          id="choose-audio-button"
          accept={acceptedFileTypes}
          aria-label={I18n.t("use_cases.transcription_page.choose_file_button")}
        />
      </Button>
    </>
  );
};

export default ChooseMediaButton;
